import React, { useState, useEffect } from 'react';
import { HiOutlineSearch, HiOutlineTrash, HiOutlineRefresh, HiOutlineX, HiOutlineUpload, HiOutlineDocumentAdd } from 'react-icons/hi';
import * as XLSX from 'xlsx';

const SutFiyatlariPage = () => {
  const [sutPrices, setSutPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  
  // Excel yükleme state'leri
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [excelDataList, setExcelDataList] = useState([]);
  const [columnMappings, setColumnMappings] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, file: '' });
  const [uploadErrorReport, setUploadErrorReport] = useState(null);
  const [uploadSuccessCount, setUploadSuccessCount] = useState(0); // Anlık başarılı sayaç
  const [uploadErrorCount, setUploadErrorCount] = useState(0); // Anlık hata sayaç

  // Kolon tespit anahtar kelimeleri
  const SUT_CODE_KEYS = [
    'sut kodu', 'sut_kodu', 'sut kodu no', 'sutcode', 'sut', 'kod', 'malzeme kodu',
    'sut kodu no', 'sut kodu numarası', 'sut kodu numarasi', 'sut kodu no',
    'sut kodu', 'sut_kodu', 'sut kodu no', 'sutcode', 'sut', 'kod', 'malzeme kodu',
    'sut kodu no', 'sut kodu numarası', 'sut kodu numarasi', 'sut kodu no',
    'sut kodu', 'sut_kodu', 'sut kodu no', 'sutcode', 'sut', 'kod', 'malzeme kodu',
    'sut kodu no', 'sut kodu numarası', 'sut kodu numarasi', 'sut kodu no'
  ];
  const PRICE_KEYS = [
    'fiyat', 'sut fiyatı', 'sut fiyati', 'sut fiyatı (tl)', 'birim fiyat', 
    'sut birim fiyatı', 'sut birim fiyati', 'sut birim fiyatı (tl)', 
    'sut birim fiyatı tl', 'sut fiyat', 'sutfiyat', 'sutfiyati', 
    'sutfiyati (tl)', 'sut birim fiyat', 'sut birimfiyat', 'sut birimfiyati', 
    'sut birimfiyati (tl)', 'sut birim fiyat tl', 'fiyat (tl)', 'fiyat tl',
    'birim fiyat (tl)', 'birim fiyat tl', 'fiyat', 'price', 'tl', 'lira'
  ];
  const DESCRIPTION_KEYS = [
    'tanım', 'tıbbi malzeme alan tanımı', 'tıbbi malzeme alan tanimi', 
    'malzeme tanımı', 'malzeme tanimi', 'ürün tanımı', 'ürün tanimi',
    'açıklama', 'aciklama', 'description', 'desc', 'tanim', 'aciklamasi',
    'tıbbi malzeme alan tanımı', 'tıbbi malzeme alan tanimi', 'malzeme alan tanımı'
  ];
  const ACKLAMA_KEYS = [
    'açıklama', 'aciklama', 'açıklama', 'aciklamasi', 'description', 'desc',
    'not', 'açıklama', 'aciklama', 'açıklama', 'aciklamasi'
  ];

  const fetchSutPrices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 50,
        ...(searchTerm && { search: searchTerm })
      });
      
      const response = await fetch(`/api/sut-prices?${params}`);
      const data = await response.json();
      
      setSutPrices(data.data);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('SUT fiyatları getirme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSutPrices();
  }, [currentPage, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSutPrices();
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/sut-prices/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchSutPrices();
        setShowDeleteModal(false);
        setDeleteItemId(null);
      }
    } catch (error) {
      console.error('Silme hatası:', error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      for (const id of selectedItems) {
        await fetch(`/api/sut-prices/${id}`, {
          method: 'DELETE'
        });
      }
      setSelectedItems([]);
      fetchSutPrices();
    } catch (error) {
      console.error('Toplu silme hatası:', error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(sutPrices.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  // Çoklu Excel dosyası yükleme
  const handleMultipleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadedFiles(files);
    const fileDataList = [];
    const mappings = {};

    files.forEach((file, index) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // İlk 10 satırı okuyarak başlık satırını bul
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          console.log(`${file.name} - İlk 5 satır:`, rows.slice(0, 5));
          
          const headerRow = findHeaderRow(rows);
          const headers = rows[headerRow];
          console.log(`${file.name} - Tespit edilen başlık satırı:`, headerRow + 1);
          console.log(`${file.name} - Başlıklar:`, headers);
          
          const mapping = detectColumnMapping(headers);
          mappings[file.name] = mapping;
          
          // Tüm veri satırlarını oluştur
          const allDataRows = rows.slice(headerRow + 1).map(row => {
            const obj = {};
            headers.forEach((h, i) => { obj[h] = row[i]; });
            return obj;
          });
          // Yüklemeye uygun satırları filtrele
          const validRows = allDataRows.filter(row => {
            const sutCode = (row[mapping.sutCode] || '').toString().trim();
            let price = row[mapping.price];
            if (typeof price === 'string') price = price.replace(',', '.').replace(/[^	d.]/g, '');
            return sutCode && price;
          });
          // Uygun olmayan satırları filtrele
          const invalidRows = allDataRows.length - validRows.length;

          fileDataList[index] = {
            fileName: file.name,
            data: validRows, // Tüm satırları kullan (yükleme için)
            previewData: validRows.slice(0, 100), // Önizleme için sadece ilk 100
            totalRows: allDataRows.length,
            validRowsCount: validRows.length,
            invalidRowsCount: invalidRows
          };

          // Tüm dosyalar işlendiğinde state'i güncelle
          if (fileDataList.filter(Boolean).length === files.length) {
            setExcelDataList(fileDataList.filter(Boolean));
            setColumnMappings(mappings);
            setShowUploadModal(true);
          }
        } catch (error) {
          console.error('Excel okuma hatası:', error);
          alert(`${file.name} dosyası okunamadı!`);
        }
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  // Başlık satırını bul
  const findHeaderRow = (rows) => {
    // İlk 10 satırı kontrol et
    for (let i = 0; i < Math.min(10, rows.length); i++) {
      const row = rows[i].map(x => (x || '').toString().toLowerCase());
      
      // SUT kodu ve fiyat kelimelerini ara
      const hasSutCode = row.some(cell => 
        cell.includes('sut') && (cell.includes('kod') || cell.includes('code'))
      );
      const hasPrice = row.some(cell => 
        cell.includes('fiyat') || cell.includes('price') || cell.includes('tl')
      );
      
      // Eğer hem SUT kodu hem fiyat bulunduysa bu başlık satırıdır
      if (hasSutCode && hasPrice) {
        console.log(`Başlık satırı bulundu: ${i + 1}. satır`);
        return i;
      }
    }
    
    // Eğer bulunamazsa, ikinci satırı varsayılan olarak kullan
    console.log('Başlık satırı bulunamadı, 2. satır kullanılıyor');
    return 1; // İkinci satır (index 1)
  };

  // Kolon eşleştirmesini tespit et
  const detectColumnMapping = (headers) => {
    const mapping = {};
    
    console.log('Tespit edilen başlıklar:', headers);
    
    // SUT kodu kolonunu bul
    for (const key of SUT_CODE_KEYS) {
      for (const header of headers) {
        if (!header) continue;
        
        const normalizedHeader = header.toString().toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        const normalizedKey = key.toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        if (normalizedHeader.includes(normalizedKey)) {
          mapping.sutCode = header;
          console.log(`SUT kodu kolonu bulundu: ${header}`);
          break;
        }
      }
      if (mapping.sutCode) break;
    }
    
    // Fiyat kolonunu bul
    for (const key of PRICE_KEYS) {
      for (const header of headers) {
        if (!header) continue;
        
        const normalizedHeader = header.toString().toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        const normalizedKey = key.toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        if (normalizedHeader.includes(normalizedKey)) {
          mapping.price = header;
          console.log(`Fiyat kolonu bulundu: ${header}`);
          break;
        }
      }
      if (mapping.price) break;
    }

    // Tanım kolonunu bul
    for (const key of DESCRIPTION_KEYS) {
      for (const header of headers) {
        if (!header) continue;
        
        const normalizedHeader = header.toString().toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        const normalizedKey = key.toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        if (normalizedHeader.includes(normalizedKey)) {
          mapping.description = header;
          console.log(`Tanım kolonu bulundu: ${header}`);
          break;
        }
      }
      if (mapping.description) break;
    }

    // Açıklama kolonunu bul
    for (const key of ACKLAMA_KEYS) {
      for (const header of headers) {
        if (!header) continue;
        
        const normalizedHeader = header.toString().toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        const normalizedKey = key.toLowerCase()
          .replace(/[ğüşöçıİ]/g, (match) => {
            const map = { 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c', 'ı': 'i', 'İ': 'i' };
            return map[match] || match;
          })
          .replace(/[^a-z0-9]/gi, '');
        
        if (normalizedHeader.includes(normalizedKey)) {
          mapping.aciklama = header;
          console.log(`Açıklama kolonu bulundu: ${header}`);
          break;
        }
      }
      if (mapping.aciklama) break;
    }
    
    console.log('Tespit edilen kolon eşleştirmesi:', mapping);
    return mapping;
  };

  // Çoklu Excel verilerini yükle
  const uploadMultipleExcelData = async () => {
    // Tüm dosyalar için kolon seçimi kontrolü
    const allColumnsSelected = excelDataList.every(fileData => {
      const mapping = columnMappings[fileData.fileName];
      return mapping && mapping.sutCode && mapping.price;
    });
    if (!allColumnsSelected) {
      alert('Tüm dosyalar için SUT kodu ve fiyat kolonlarını seçmelisiniz!');
      return;
    }

    setUploadLoading(true);
    
    let totalSuccess = 0;
    let totalError = 0;
    let totalProcessed = 0;
    let totalToProcess = 0;

    // Toplam işlenecek satır sayısını hesapla
    excelDataList.forEach(fileData => {
      totalToProcess += fileData.data.length;
    });

    // Eğer hiç geçerli satır yoksa uyarı ver ve modalı kapatma
    if (totalToProcess === 0) {
      alert('Yüklenecek geçerli satır bulunamadı! Lütfen dosya(lar)da SUT kodu ve fiyatı dolu satır olduğundan emin olun.');
      setUploadLoading(false);
      return;
    }

    setUploadProgress({ current: 0, total: totalToProcess, file: '' });
    setUploadSuccessCount(0); // Sayaçları sıfırla
    setUploadErrorCount(0);

    let errorDetails = [];
    let fileUploadStats = {};

    try {
      for (const fileData of excelDataList) {
        const mapping = columnMappings[fileData.fileName];
        
        if (!mapping.sutCode || !mapping.price) {
          errorDetails.push({
            file: fileData.fileName,
            sut_code: '',
            price: '',
            reason: 'Kolon tespit edilemedi'
          });
          continue;
        }

        setUploadProgress(prev => ({ ...prev, file: fileData.fileName }));

        // Sadece SUT kodu ve fiyatı dolu olan satırları yükle
        const validRows = fileData.data.filter(row => {
          const sutCode = (row[mapping.sutCode] || '').toString().trim();
          let price = row[mapping.price];
          if (typeof price === 'string') price = price.replace(',', '.').replace(/[^\d.]/g, '');
          return sutCode && price;
        });

        fileUploadStats[fileData.fileName] = { success: 0, error: 0, total: validRows.length };

        for (let i = 0; i < validRows.length; i++) {
          const row = validRows[i];
          const sutCode = (row[mapping.sutCode] || '').toString().replace(/\s/g, '').toUpperCase();
          let price = row[mapping.price];
          let priceNum = price;
          if (typeof price === 'string') {
            priceNum = parseFloat(price.replace(',', '.').replace(/[^\d.]/g, ''));
          }

          let errorReason = '';
          if (!sutCode) errorReason = 'SUT kodu boş';
          else if (!price) errorReason = 'Fiyat boş';
          else if (isNaN(priceNum) || priceNum <= 0) errorReason = 'Geçersiz fiyat';

          if (errorReason) {
            totalError++;
            setUploadErrorCount(prev => prev + 1); // Hatalı sayaç güncelle
            fileUploadStats[fileData.fileName].error++;
            errorDetails.push({
              file: fileData.fileName,
              sut_code: sutCode,
              price: price,
              reason: errorReason
            });
            totalProcessed++;
            setUploadProgress({ current: totalProcessed, total: totalToProcess, file: fileData.fileName });
            continue;
          }

          try {
            const roundedPrice = Math.round((priceNum + Number.EPSILON) * 100) / 100;
            
            // AbortController ile timeout ekle
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 saniye timeout
            
            const mapping = columnMappings[fileData.fileName];
            const description = mapping.description ? row[mapping.description] : null;
            const aciklama = mapping.aciklama ? row[mapping.aciklama] : null;

            const response = await fetch('/api/sut-prices', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sut_code: sutCode,
                price: roundedPrice,
                currency: 'TL',
                source_file: fileData.fileName,
                row_info: JSON.stringify(row),
                group_id: null,
                description: description,
                aciklama: aciklama
              }),
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);

            if (response.ok) {
              const responseData = await response.json();
              totalSuccess++;
              setUploadSuccessCount(prev => prev + 1);
              fileUploadStats[fileData.fileName].success++;
              
              // Güncelleme veya ekleme durumunu logla
              if (responseData.action === 'updated') {
                console.log(`SUT kodu güncellendi: ${sutCode}`);
              } else if (responseData.action === 'inserted') {
                console.log(`Yeni SUT kodu eklendi: ${sutCode}`);
              }
            } else {
              totalError++;
              setUploadErrorCount(prev => prev + 1);
              fileUploadStats[fileData.fileName].error++;
              let res = {};
              try {
                res = await response.json();
              } catch {}
              errorDetails.push({
                file: fileData.fileName,
                sut_code: sutCode,
                price: roundedPrice,
                reason: res.error || res.message || response.statusText || 'Backend hata'
              });
            }
            
            // Her 10 satırda bir küçük bekleme (rate limiting)
            if (totalProcessed % 10 === 0) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            
          } catch (error) {
            totalError++;
            setUploadErrorCount(prev => prev + 1);
            fileUploadStats[fileData.fileName].error++;
            
            let errorMessage = 'İstek hatası';
            if (error.name === 'AbortError') {
              errorMessage = 'Timeout hatası (30 saniye)';
            } else if (error.message) {
              errorMessage = error.message;
            }
            
            errorDetails.push({
              file: fileData.fileName,
              sut_code: sutCode,
              price: price,
              reason: errorMessage
            });
          }
          totalProcessed++;
          setUploadProgress({ current: totalProcessed, total: totalToProcess, file: fileData.fileName });
        }
      }

      setUploadErrorReport({
        totalSuccess,
        totalError,
        totalProcessed,
        errorDetails,
        fileUploadStats,
        excelDataList // dosya başına toplam/uygun/uygun olmayan
      });
      setShowUploadModal(false);
      setUploadedFiles([]);
      setExcelDataList([]);
      setColumnMappings({});
      fetchSutPrices();
    } catch (error) {
      console.error('Yükleme hatası:', error);
      alert('Yükleme sırasında hata oluştu!');
      // Hata olursa modalı kapatma
      setUploadLoading(false);
      return;
    } finally {
      setUploadLoading(false);
      setUploadProgress({ current: 0, total: 0, file: '' });
      setUploadSuccessCount(0); // Yükleme bitince sayaçları sıfırla
      setUploadErrorCount(0);
    }
  };

  // Dosya listesinden dosya çıkarma fonksiyonu
  const handleRemoveFile = (fileName) => {
    setExcelDataList(prev => prev.filter(f => f.fileName !== fileName));
    setUploadedFiles(prev => prev.filter(f => f.name !== fileName));
    setColumnMappings(prev => {
      const copy = { ...prev };
      delete copy[fileName];
      return copy;
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">SUT Fiyat Listesi</h1>
        <p className="text-gray-600">Yüklenen SUT fiyatlarını görüntüleyin ve yönetin</p>
      </div>

      {/* Arama ve İşlemler */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="SUT kodu veya dosya adı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>
          
          <div className="flex gap-2">
            <label className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer">
              <HiOutlineUpload className="h-4 w-4" />
              Çoklu Excel Yükle
              <input
                type="file"
                accept=".xlsx,.xls"
                multiple
                onChange={handleMultipleFileUpload}
                className="hidden"
              />
            </label>
            
            <button
              onClick={fetchSutPrices}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <HiOutlineRefresh className="h-4 w-4" />
              Yenile
            </button>
            
            {selectedItems.length > 0 && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <HiOutlineTrash className="h-4 w-4" />
                Seçili ({selectedItems.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedItems.length === sutPrices.length && sutPrices.length > 0}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SUT Kodu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fiyat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Para Birimi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kaynak Dosya
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Yüklenme Tarihi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Yükleniyor...
                  </td>
                </tr>
              ) : sutPrices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    SUT fiyatı bulunamadı
                  </td>
                </tr>
              ) : (
                sutPrices.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.sut_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPrice(item.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.currency}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {item.source_file}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setDeleteItemId(item.id);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <HiOutlineTrash className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Sayfa {currentPage} / {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Önceki
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}

      {/* Çoklu Excel Yükleme Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Çoklu Excel Dosyası Yükleme
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFiles([]);
                  setExcelDataList([]);
                  setColumnMappings({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiOutlineX className="h-6 w-6" />
              </button>
            </div>

            {/* Dosya Listesi */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Yüklenecek Dosyalar:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {excelDataList.map((fileData, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3 relative">
                    <button
                      onClick={() => handleRemoveFile(fileData.fileName)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-600"
                      title="Dosyayı kaldır"
                    >
                      <HiOutlineX className="h-5 w-5" />
                    </button>
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-sm">{fileData.fileName}</span>
                      <span className="text-xs text-gray-500">{fileData.totalRows} satır</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <label className="block text-gray-600 mb-1">SUT Kodu:</label>
                        <select
                          value={columnMappings[fileData.fileName]?.sutCode || ''}
                          onChange={(e) => setColumnMappings(prev => ({
                            ...prev,
                            [fileData.fileName]: { ...prev[fileData.fileName], sutCode: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="">Kolon seçin...</option>
                          {fileData.data.length > 0 && Object.keys(fileData.data[0] || {}).map(key => (
                            <option key={key} value={key}>{key}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Fiyat:</label>
                        <select
                          value={columnMappings[fileData.fileName]?.price || ''}
                          onChange={(e) => setColumnMappings(prev => ({
                            ...prev,
                            [fileData.fileName]: { ...prev[fileData.fileName], price: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="">Kolon seçin...</option>
                          {fileData.data.length > 0 && Object.keys(fileData.data[0] || {}).map(key => (
                            <option key={key} value={key}>{key}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                      <div>
                        <label className="block text-gray-600 mb-1">Tanım:</label>
                        <select
                          value={columnMappings[fileData.fileName]?.description || ''}
                          onChange={(e) => setColumnMappings(prev => ({
                            ...prev,
                            [fileData.fileName]: { ...prev[fileData.fileName], description: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="">Kolon seçin...</option>
                          {fileData.data.length > 0 && Object.keys(fileData.data[0] || {}).map(key => (
                            <option key={key} value={key}>{key}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Açıklama:</label>
                        <select
                          value={columnMappings[fileData.fileName]?.aciklama || ''}
                          onChange={(e) => setColumnMappings(prev => ({
                            ...prev,
                            [fileData.fileName]: { ...prev[fileData.fileName], aciklama: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="">Kolon seçin...</option>
                          {fileData.data.length > 0 && Object.keys(fileData.data[0] || {}).map(key => (
                            <option key={key} value={key}>{key}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-xs mt-2">
                      <span>Toplam satır: <b>{fileData.totalRows}</b></span>
                      <span>Yüklemeye uygun: <b>{fileData.validRowsCount}</b></span>
                      <span>Uygun olmayan: <b>{fileData.invalidRowsCount}</b></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Önizleme */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Önizleme (İlk 5 satır):</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left border-b">Dosya</th>
                      <th className="px-3 py-2 text-left border-b">SUT Kodu</th>
                      <th className="px-3 py-2 text-left border-b">Fiyat</th>
                      <th className="px-3 py-2 text-left border-b">Diğer Veriler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelDataList.slice(0, 3).flatMap((fileData, fileIndex) => 
                      fileData.previewData.slice(0, 5).map((row, rowIndex) => {
                        const mapping = columnMappings[fileData.fileName];
                        return (
                          <tr key={`${fileIndex}-${rowIndex}`} className="border-b">
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {fileData.fileName}
                            </td>
                            <td className="px-3 py-2">
                              {mapping?.sutCode ? row[mapping.sutCode] : '-'}
                            </td>
                            <td className="px-3 py-2">
                              {mapping?.price ? row[mapping.price] : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {Object.entries(row).filter(([key]) => key !== mapping?.sutCode && key !== mapping?.price).slice(0, 2).map(([key, value]) => `${key}: ${value}`).join(', ')}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Yükleme İlerlemesi */}
            {uploadLoading && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Yükleniyor: {uploadProgress.file}</span>
                  <span>{uploadProgress.current} / {uploadProgress.total}</span>
                </div>
                <div className="flex gap-4 text-xs mb-2">
                  <span className="text-green-700 font-semibold">Başarılı: {uploadSuccessCount}</span>
                  <span className="text-red-700 font-semibold">Hatalı: {uploadErrorCount}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFiles([]);
                  setExcelDataList([]);
                  setColumnMappings({});
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={uploadLoading}
              >
                İptal
              </button>
              <button
                onClick={uploadMultipleExcelData}
                disabled={
                  uploadLoading ||
                  excelDataList.length === 0 ||
                  !excelDataList.every(fileData => {
                    const mapping = columnMappings[fileData.fileName];
                    return mapping && mapping.sutCode && mapping.price;
                  })
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadLoading ? 'Yükleniyor...' : `${excelDataList.length} Dosyayı Yükle`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hata Ayıklama Raporu Modalı */}
      {uploadErrorReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Yükleme Sonucu</h3>
            <div className="mb-4">
              <span className="text-green-700 font-semibold">Başarılı: {uploadErrorReport.totalSuccess}</span>
              <span className="mx-4 text-red-700 font-semibold">Hatalı: {uploadErrorReport.totalError}</span>
              <span className="text-gray-700">Toplam: {uploadErrorReport.totalProcessed}</span>
              <div className="mt-2 text-sm text-blue-600">
                <p>💡 <strong>Not:</strong> Aynı SUT kodu varsa mevcut kayıt güncellenir, yeni satır eklenmez.</p>
              </div>
            </div>
            {uploadErrorReport.errorDetails.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Hatalı Satırlar (ilk 10):</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left border-b">Dosya</th>
                        <th className="px-3 py-2 text-left border-b">SUT Kodu</th>
                        <th className="px-3 py-2 text-left border-b">Fiyat</th>
                        <th className="px-3 py-2 text-left border-b">Hata Nedeni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadErrorReport.errorDetails.slice(0, 10).map((err, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-3 py-2 text-xs text-gray-500">{err.file}</td>
                          <td className="px-3 py-2">{err.sut_code}</td>
                          <td className="px-3 py-2">{err.price}</td>
                          <td className="px-3 py-2 text-xs text-red-600">{err.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Dosya Bazında Yükleme Özeti:</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 border-b">Dosya</th>
                      <th className="px-2 py-1 border-b">Toplam</th>
                      <th className="px-2 py-1 border-b">Uygun</th>
                      <th className="px-2 py-1 border-b">Uygun Olmayan</th>
                      <th className="px-2 py-1 border-b">Başarılı</th>
                      <th className="px-2 py-1 border-b">Hatalı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadErrorReport.excelDataList.map((fileData, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-1 border-b">{fileData.fileName}</td>
                        <td className="px-2 py-1 border-b">{fileData.totalRows}</td>
                        <td className="px-2 py-1 border-b">{fileData.validRowsCount}</td>
                        <td className="px-2 py-1 border-b">{fileData.invalidRowsCount}</td>
                        <td className="px-2 py-1 border-b text-green-700 font-semibold">{uploadErrorReport.fileUploadStats[fileData.fileName]?.success}</td>
                        <td className="px-2 py-1 border-b text-red-700 font-semibold">{uploadErrorReport.fileUploadStats[fileData.fileName]?.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setUploadErrorReport(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {selectedItems.length > 0 ? 'Seçili SUT Fiyatlarını Sil' : 'SUT Fiyatını Sil'}
            </h3>
            <p className="text-gray-600 mb-6">
              {selectedItems.length > 0 
                ? `${selectedItems.length} adet SUT fiyatını silmek istediğinizden emin misiniz?`
                : 'Bu SUT fiyatını silmek istediğinizden emin misiniz?'
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteItemId(null);
                  setSelectedItems([]);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  if (selectedItems.length > 0) {
                    handleBulkDelete();
                  } else if (deleteItemId) {
                    handleDelete(deleteItemId);
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SutFiyatlariPage; 